// Động Phủ `character.cave` (UI_SPEC §18.1): mỗi công trình là một vật thể trên tranh. P0: Dược Viên
// (luống, niên phân, chip giờ tới lúc chín, thu hoạch) và Đan Phòng (lò, cửa Luyện Đan).
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '@i18n/t';
import { remainingText } from '@i18n/time';
import { formatFull } from '@i18n/format';
import { herbDef } from '@data/crafting';
import { isMature, plotMatureAtMs, plotYears } from '@sim/crafting';
import { cave, caveGrowth, harvest, harvestAll } from '@state/modules/cave';
import { openWindow } from '@state/modules/ui';
import { ItemSlot } from '../../kit/ItemSlot';
import { TextButton } from '../../kit/TextButton';
import { Icon } from '../../icons';
import { rarityClass } from '../../itemView';
import { useClock } from '../../useClock';
import { goodsDef } from '@data/items/goods';

export function CaveScreen(): JSX.Element {
  const c = cave.value;
  const g = caveGrowth.value;
  const clock = useClock();
  const [flash, setFlash] = useState<number | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (flashTimer.current !== null) clearTimeout(flashTimer.current);
    },
    [],
  );
  const ripe = c.plots.filter((p) => isMature(p, clock, g)).length;
  const busy = c.jobs.length;
  return (
    <>
      <div class="ht-caption" data-id="caption">
        <b>{t('site.lk.servantQuarters.name')}</b>
        <span>{t('ui.cave.sub', { g: c.buildings.herbGarden.lv, p: c.buildings.pillRoom.lv })}</span>
      </div>

      <section class="cave-obj garden" data-id="cave-garden">
        <button
          type="button"
          class="cave-title"
          onClick={() => openWindow({ kind: 'building', id: 'herbGarden' })}
        >
          <Icon name="garden" />
          {t('craft.building.herbGarden')} · {t('ui.cave.lv', { n: c.buildings.herbGarden.lv })}
          {c.buildings.herbGarden.upgrade && (
            <small> · {remainingText(c.buildings.herbGarden.upgrade.endMs - clock)}</small>
          )}
        </button>
        <div class="cave-plots">
          {c.plots.map((p, i) => {
            const mature = isMature(p, clock, g);
            const years = plotYears(p, clock, g);
            return (
              <div class="cave-plot" key={i}>
                <ItemSlot
                  id={`plot-${i}`}
                  size="md"
                  q={p.herbId ? rarityClass(goodsDef(p.herbId).tier) : undefined}
                  glyph="herb"
                  state={p.herbId ? (mature ? 'normal' : 'off') : 'empty'}
                  signal={mature ? 'task' : null}
                  selected={flash === i}
                  bl={mature ? t('ui.cave.years', { n: formatFull(Math.floor(years)) }) : undefined}
                  label={p.herbId ? t(`${p.herbId}.name`) : t('ui.cave.plotEmpty')}
                  onClick={() => {
                    // Chạm thứ hai ngay sau khi thu (ô đang loé) không mở cửa gieo.
                    if (flash === i) return;
                    if (mature && harvest(i)) {
                      setFlash(i);
                      if (flashTimer.current !== null) clearTimeout(flashTimer.current);
                      flashTimer.current = setTimeout(() => setFlash(null), 620);
                    } else openWindow({ kind: 'plot', index: i });
                  }}
                />
                <small>
                  {p.herbId
                    ? mature
                      ? t(`${p.herbId}.name`)
                      : remainingText(plotMatureAtMs(p, g) - clock)
                    : t('ui.cave.plant')}
                </small>
              </div>
            );
          })}
        </div>
        {ripe > 1 && (
          <TextButton id="cave-harvest-all" onClick={() => harvestAll()}>
            {t('ui.cave.harvestAll', { n: ripe })}
          </TextButton>
        )}
      </section>

      <section class="cave-obj furnace" data-id="cave-furnace">
        <button
          type="button"
          class="cave-title"
          onClick={() => openWindow({ kind: 'building', id: 'pillRoom' })}
        >
          <Icon name="furnace" />
          {t('craft.building.pillRoom')} · {t('ui.cave.lv', { n: c.buildings.pillRoom.lv })}
          {c.buildings.pillRoom.upgrade && (
            <small> · {remainingText(c.buildings.pillRoom.upgrade.endMs - clock)}</small>
          )}
        </button>
        <button
          type="button"
          class="cave-furnace"
          data-id="cave-alchemy"
          onClick={() => openWindow({ kind: 'alchemy' })}
        >
          <Icon name="furnace" />
          <span>
            {busy
              ? t('ui.cave.furnaceBusy', {
                  left: remainingText(Math.min(...c.jobs.map((j) => j.endMs)) - clock),
                  n: busy,
                })
              : t('ui.cave.furnaceIdle')}
          </span>
        </button>
      </section>
    </>
  );
}

export { herbDef };
